import { useState } from 'react'
import {
  Container,
  Typography,
  Button,
  Box,
  Stack,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material'
import { Add, ShoppingCart } from '@mui/icons-material'
import { useSupplyStore } from '@/store/useSupplyStore'
import SupplyCard from '@/components/inventory/SupplyCard'
import SupplyForm from '@/components/inventory/SupplyForm'
import type { Supply } from '@/types'

export default function InventoryPage() {
  const [formOpen, setFormOpen] = useState(false)
  const [editingSupply, setEditingSupply] = useState<Supply | null>(null)

  const supplies = useSupplyStore((state) => state.supplies)
  const deleteSupply = useSupplyStore((state) => state.deleteSupply)
  const getLowInventory = useSupplyStore((state) => state.getLowInventory)

  const lowInventory = getLowInventory()

  const handleEdit = (supply: Supply) => {
    setEditingSupply(supply)
    setFormOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this supply?')) {
      await deleteSupply(id)
    }
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingSupply(null)
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Inventory</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            Add Supply
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary">
          Manage cleaning supplies and get low stock alerts
        </Typography>
      </Box>

      {/* Low Inventory Alert */}
      {lowInventory.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Low Stock Alert</AlertTitle>
          {lowInventory.length} {lowInventory.length === 1 ? 'item needs' : 'items need'}{' '}
          restocking
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Supplies
              </Typography>
              <Typography variant="h4">{supplies.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Low Stock Items
              </Typography>
              <Typography variant="h4" color="warning.main">
                {lowInventory.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Value
              </Typography>
              <Typography variant="h4">
                $
                {supplies
                  .reduce((sum, s) => sum + (s.cost || 0) * s.quantity, 0)
                  .toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Shopping List */}
      {lowInventory.length > 0 && (
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <ShoppingCart />
            <Typography variant="h5">Shopping List</Typography>
            <Chip label={lowInventory.length} color="warning" size="small" />
          </Box>

          <Stack spacing={2}>
            {lowInventory.map((supply) => (
              <SupplyCard
                key={supply.id}
                supply={supply}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* All Supplies */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          All Supplies
        </Typography>

        {supplies.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>No Supplies Yet</AlertTitle>
            Add your first cleaning supply to start tracking inventory!
          </Alert>
        ) : (
          <Stack spacing={2}>
            {supplies.map((supply) => (
              <SupplyCard
                key={supply.id}
                supply={supply}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        )}
      </Box>

      {/* Supply Form Dialog */}
      <SupplyForm
        open={formOpen}
        onClose={handleCloseForm}
        supply={editingSupply}
      />
    </Container>
  )
}
